import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ArrowLeftIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import {
  DetailSidebarLayout,
  EntityTagsInline,
  StatusBadge,
} from "../../../components";
import { Badge, Button, Spinner } from "../../../shared";
import { useCrumbs } from "../../../hooks/useCrumbs";
import { usePermissions } from "../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../constants/access-control";
import { capitalizeFirstLetter } from "../../../utils";
import { getEquipmentComplianceAlerts } from "../../../utils/equipmentCompliance";
import {
  useGetEquipmentByIdQuery,
  useUpdateEquipmentMutation,
} from "../../../redux/services/equipment";
import { DeleteEquipmentModal } from "../DeleteModal";
import EquipmentOverview from "./Overview";
import EquipmentMaintenance from "./Maintenance";
import { EquipmentFleetHub } from "./FleetHub";
import { useConfig } from "../../../hooks";
import COLUMN_DEFINITIONS from "./tableconfig";

/**
 * Fleet / equipment detail page with overview and maintenance tabs.
 */
export const EquipmentShow = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const crumbs = useCrumbs();
  const { appType } = useConfig();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission(PERMISSIONS.EQUIPMENT_EDIT);
  const canDelete = hasPermission(PERMISSIONS.EQUIPMENT_DELETE);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const { data: equipment, isLoading, isError } = useGetEquipmentByIdQuery(
    { id },
    { skip: !id }
  );
  const [updateEquipment] = useUpdateEquipmentMutation();

  const showFleetHub = useMemo(() => {
    if (!equipment) return false;
    return (
      appType === "trucking" &&
      hasPermission(PERMISSIONS.FLEET_VIEW) &&
      ["truck", "trailer", "chassis"].includes(equipment.equipmentCategory)
    );
  }, [appType, equipment, hasPermission]);

  useEffect(() => {
    if (equipment?._id) {
      crumbs.updateCrumbName({
        name: equipment.name || "Equipment",
        path: `/equipment/${equipment._id}`,
      });
      crumbs.setLoading(false);
    }
  }, [equipment?._id, equipment?.name]);

  const handleSave = useCallback(
    async (data) => {
      try {
        await updateEquipment({ id, ...data }).unwrap();
        toast.success("Equipment updated");
      } catch {
        toast.error("Failed to update equipment");
      }
    },
    [id, updateEquipment]
  );

  const maintenanceHistory = equipment?.maintenanceHistory || [];

  const complianceAlerts = useMemo(
    () => (equipment ? getEquipmentComplianceAlerts(equipment) : []),
    [equipment]
  );

  const tabs = useMemo(
    () => [
      {
        id: "Overview",
        content: (
          <EquipmentOverview
            equipment={equipment}
            maintenanceHistory={maintenanceHistory}
            onUpdate={handleSave}
          />
        ),
      },
      {
        id: "Maintenance",
        content: (
          <div className="h-full min-h-0">
            <EquipmentMaintenance
              equipment={equipment}
              maintenanceHistory={maintenanceHistory}
              tableConfig={COLUMN_DEFINITIONS}
            />
          </div>
        ),
      },
      ...(showFleetHub
        ? [
            {
              id: "Fleet",
              content: <EquipmentFleetHub equipmentId={id} />,
            },
          ]
        : []),
    ],
    [equipment, maintenanceHistory, handleSave, showFleetHub, id]
  );

  const title = useMemo(() => {
    if (!equipment) {
      return <span className="text-lg font-semibold text-gray-800">Equipment</span>;
    }
    return (
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <span className="truncate text-lg font-semibold text-gray-800">
          {equipment.name || "Equipment"}
        </span>
        {equipment.equipmentCategory ? (
          <Badge
            label={capitalizeFirstLetter(equipment.equipmentCategory)}
            color="blue"
          />
        ) : null}
        <StatusBadge status={equipment.status} />
        {complianceAlerts.map((alert) => (
          <Badge key={alert.key} label={alert.label} color={alert.color} />
        ))}
        {equipment._id ? (
          <EntityTagsInline
            entity={equipment}
            appliesTo="equipment"
            entityLabel="Equipment"
            editable={canEdit}
            onChangeTags={(tags) => handleSave({ tags })}
            badgeSize="lg"
          />
        ) : null}
      </div>
    );
  }, [equipment, complianceAlerts, canEdit, handleSave]);

  const actions = useMemo(() => {
    const items = [
      <Button
        key="back"
        type="alternative"
        size="xs"
        onClick={() => {
          const category = equipment?.equipmentCategory;
          if (category === "truck" || category === "trailer") {
            navigate(`/equipment?category=${category}`);
            return;
          }
          navigate("/equipment");
        }}
      >
        <ArrowLeftIcon className="mr-1 inline h-4 w-4" />
        Back to fleet
      </Button>,
    ];

    if (canDelete) {
      items.push(
        <Button
          key="delete"
          type="danger"
          size="xs"
          onClick={() => setDeleteModalOpen(true)}
        >
          <TrashIcon className="mr-1 inline h-4 w-4" />
          Delete
        </Button>
      );
    }

    return <div className="flex flex-wrap items-center gap-2">{items}</div>;
  }, [canDelete, equipment?.equipmentCategory, navigate]);

  const renderHeader = useCallback(
    (headerProps) => (
      <div className="border-b bg-white px-3 py-2">
        <div className="flex items-center justify-between gap-2">
          {headerProps.title}
          {headerProps.actions}
        </div>
      </div>
    ),
    []
  );

  /** Keep tab selection in sync with `?equipment-show=` (required when useSearchParams is on). */
  const handleTabClick = useCallback(
    (_e, _item, path) => {
      navigate(path);
    },
    [navigate]
  );

  if (isLoading) {
    return (
      <div className="flex h-full min-h-[240px] items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (isError || !equipment) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-6">
        <p className="text-gray-600">Unable to load this equipment record.</p>
        <Button type="alternative" onClick={() => navigate("/equipment")}>
          Return to fleet list
        </Button>
      </div>
    );
  }

  return (
    <div className="relative flex h-full min-h-0 flex-col">
      <DetailSidebarLayout
        layout="page"
        title={title}
        header={renderHeader}
        actions={actions}
        hideSidebar
        splitStorageKey="equipment-show-layout"
        tabs={tabs}
        tabsProps={{
          useSearchParams: true,
          id: "equipment-show",
          onClick: handleTabClick,
        }}
      />
      <DeleteEquipmentModal
        visible={deleteModalOpen}
        setVisible={setDeleteModalOpen}
        equipment={equipment ? [equipment] : []}
      />
    </div>
  );
};

export default EquipmentShow;
